import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';

const AuthCallback = () => {
  const navigate = useNavigate();
  const { user, profile, loading, isProfileComplete, hasRequiredDocuments } = useAuth();
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const checkProfileAndDocuments = async () => {
      if (loading) return;

      if (!user) {
        navigate('/login');
        return;
      }

      try {
        // Check if profile is complete
        const profileComplete = isProfileComplete();
        const documentsUploaded = await hasRequiredDocuments();

        if (!profileComplete || !documentsUploaded) {
          // Redirect to profile completion page
          navigate('/profile-completion');
          return;
        }

        // Check verification status
        if (profile?.verification_status === 'pending') {
          navigate('/pending-approval');
          return;
        }

        if (profile?.verification_status === 'rejected') {
          navigate('/application-rejected');
          return;
        }

        if (profile?.verification_status === 'verified') {
          navigate('/dashboard');
          return;
        }

        // Default fallback
        navigate('/profile-completion');
      } catch (err) {
        console.error('Error checking profile:', err);
        setError(err?.message || 'Failed to verify profile status');
      } finally {
        setChecking(false);
      }
    };

    checkProfileAndDocuments();
  }, [user, profile, loading, navigate, isProfileComplete, hasRequiredDocuments]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="clinical-card p-8 max-w-md w-full">
          <div className="flex items-center gap-3 mb-4">
            <Icon name="AlertCircle" size={32} color="var(--color-error)" />
            <h2 className="text-2xl font-bold text-foreground">Authentication Error</h2>
          </div>
          <p className="text-muted-foreground mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="clinical-card p-8 text-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <h2 className="text-xl font-semibold text-foreground mb-2">Verifying Your Account</h2>
        <p className="text-muted-foreground">Please wait while we check your profile...</p>
      </div>
    </div>
  );
};

export default AuthCallback;